import ApplicationComponent from "legacy/ApplicationComponent"
import BackoffTimer from "legacy/BackoffTimer"
import NextButton from "legacy/boater-reservation-flow/NextButton"
import {
  badgeForPaymentStatus,
  buildRoute,
  centsToDollars,
  humanize,
} from "legacy/helpers.es6"
import PaymentSelectSaleModal from "legacy/sales/PaymentSelectSaleModal"
import ReceiptPrinterChooser from "legacy/sales/ReceiptPrinterChooser"
import SaleCancelModal from "legacy/sales/SaleCancelModal"
import SaleRefundModal from "legacy/sales/SaleRefundModal"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"
import { v4 as uuidv4 } from "uuid"

class SaleDetails extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      printingReceipt: false,
      printerId: null,
    }

    this.handlePrintReceipt = this.handlePrintReceipt.bind(this)
    this.handleProcessing = this.handleProcessing.bind(this)
    this.handlePoll = this.handlePoll.bind(this)
    this.backoffTimer = new BackoffTimer(this.handlePoll)
    this.renderPosItems = this.renderPosItems.bind(this)
    this.renderCustomerNote = this.renderCustomerNote.bind(this)
    this.renderTotals = this.renderTotals.bind(this)
    this.renderPaymentMethod = this.renderPaymentMethod.bind(this)
    this.renderTransactions = this.renderTransactions.bind(this)
    this.renderSaleStateActionButtons =
      this.renderSaleStateActionButtons.bind(this)
    this.renderPrintReceiptButton = this.renderPrintReceiptButton.bind(this)
    this.receiptPrintersAvailable = this.receiptPrintersAvailable.bind(this)
    this.isFinishedProcessing = this.isFinishedProcessing.bind(this)
    this.statusForPayment = this.statusForPayment.bind(this)
    this.shouldDisplayTransaction = this.shouldDisplayTransaction.bind(this)
    this.shouldDisplayActions = this.shouldDisplayActions.bind(this)
    this.shouldDisplayRefundButton = this.shouldDisplayRefundButton.bind(this)
    this.shouldDisplaySaleStateActions =
      this.shouldDisplaySaleStateActions.bind(this)
  }

  componentDidMount() {
    if (
      this.props.sale.status === "processing" ||
      this.props.sale.status === "scheduled"
    ) {
      this.handleProcessing(this.props.sale)
    }
  }

  componentDidUpdate(prevProps) {
    // When the sale changes in any way (new status for example), update our state
    if (prevProps.sale !== this.props.sale) {
      // When the sale is switched to a different one, stop refreshing
      if (prevProps.sale.id !== this.props.sale.id) {
        this.backoffTimer.stop()
      }
      this.setState({ sale: this.props.sale })
    }
  }

  componentWillUnmount() {
    this.backoffTimer.stop()
  }

  handleProcessing(sale) {
    this.props.setSelectedSale(sale)
    this.backoffTimer.start()
  }

  handlePoll() {
    // Note: jQuery ($) is loaded globally
    $.ajax({
      url: buildRoute($context.pollEndpoint, { sale_id: this.props.sale.id }),
      type: "GET",
      dataType: "json",
      contentType: "application/json",
      success: (sale) => {
        if (this.isFinishedProcessing(sale)) {
          this.backoffTimer.stop()
        }
        this.props.setSelectedSale(sale)
      },
      error: (data) => {
        // no-op
      },
    })
  }

  receiptPrintersAvailable() {
    return (
      $context.receiptPrinters &&
      $context.receiptPrinters.length > 0 &&
      $context.printPaperReceiptUrl !== null
    )
  }

  isFinishedProcessing(sale) {
    return sale.status !== "processing" && sale.status !== "scheduled"
  }

  statusForPayment(scheduledPayment) {
    if (
      scheduledPayment.totalPrice.includes("-") &&
      scheduledPayment.status === "paid"
    ) {
      return "refund"
    } else if (this.props.sale.status === "canceled") {
      return "canceled"
    } else if (scheduledPayment.status === "scheduled") {
      return "processing"
    }
    return scheduledPayment.status
  }

  shouldDisplayTransaction(scheduledPayment) {
    return (
      scheduledPayment.totalPrice > 0 || this.props.sale.status !== "canceled"
    )
  }

  shouldDisplayActions(scheduledPayment) {
    return (
      scheduledPayment.status === "failed" &&
      this.props.sale.status !== "canceled"
    )
  }

  shouldDisplayRefundButton() {
    return this.props.sale.status === "paid" && this.props.sale.totalPrice > 0
  }

  shouldDisplaySaleStateActions(scheduledPayment, lastId) {
    return scheduledPayment.status === "paid" && scheduledPayment.id === lastId
  }

  renderPosItems() {
    return this.props.sale.posItems.map((posItem) => {
      const discount =
        posItem.namedDiscounts && posItem.namedDiscounts.length > 0
      return (
        <div
          key={posItem.id}
          className="grid grid-cols-6 place-items-start gap-x-3 md:gap-x-8"
        >
          <div className="col-span-3 m-0">
            <p className="m-0 inline-block pl-3">{humanize(posItem.name)}</p>
            {discount &&
              posItem.namedDiscounts.map((namedDiscount) => (
                <p
                  key={`discount-${uuidv4()}`}
                  className="ml-2 inline-block text-teal-700"
                >
                  {namedDiscount.displayText}
                </p>
              ))}
          </div>
          <p className="m-0">{posItem.quantity}</p>
          <div className="col-span-2 m-0 justify-self-end pr-3">
            {discount && (
              <p className="m-0 mr-2 inline-block line-through">
                {centsToDollars(posItem.basePreTaxAmount)}
              </p>
            )}
            <p className="m-0 inline-block">
              {centsToDollars(posItem.preTaxAmount)}
            </p>
          </div>
          <hr className="col-span-full my-5 w-full" />
        </div>
      )
    })
  }

  renderCustomerNote() {
    if (this.props.sale.note) {
      return (
        <div className="">
          <h3 className="mt-0 px-3 text-sm font-semibold">
            Message to Customer
          </h3>
          <p className="mb-4 px-3">{this.props.sale.note}</p>
        </div>
      )
    }
  }

  renderTotals() {
    return (
      <div>
        <div className="grid grid-cols-6">
          <div className="col-span-full grid grid-cols-2 rounded bg-gray-100 p-3 md:col-span-4 md:col-start-3">
            <p className="m-0 mb-3 font-semibold">Subtotal</p>
            <p className="m-0 mb-3 justify-self-end">
              {centsToDollars(this.props.sale.subtotal)}
            </p>
            <hr className="col-span-full m-0 w-full" />
            <p className="m-0 mt-3 font-semibold">{$context.salesTaxLabel}</p>
            <p className="m-0 mt-3 justify-self-end">
              {centsToDollars(this.props.sale.taxAmount)}
            </p>
          </div>
          <h3 className="ml-3 text-sm font-semibold md:col-start-3">Total</h3>
          <p className="col-start-4 m-3 justify-self-end text-2xl font-semibold md:col-start-6">
            {this.props.sale.status === "refunded"
              ? centsToDollars(this.props.sale.refundedAmount)
              : centsToDollars(this.props.sale.totalPrice)}
          </p>
          {this.shouldDisplayRefundButton() && (
            <SaleRefundModal
              sale={this.props.sale}
              refundEndpoint={buildRoute($context.refundEndpoint, {
                sale_id: this.props.sale.id,
              })}
              handleSuccess={this.handleProcessing}
              buttonClasses="col-start-3 md:col-start-6 place-self-center md:place-self-end mr-0 md:mr-2 col-span-2 md:col-span-1"
            />
          )}
        </div>
      </div>
    )
  }

  renderPaymentMethod(scheduledPayment) {
    switch (scheduledPayment.method) {
      case "credit":
        if (
          scheduledPayment.paymentIntent &&
          scheduledPayment.paymentIntent.payment_method_object
        ) {
          return (
            <div className="flex items-center space-x-1">
              <img
                src={scheduledPayment.paymentIntent.payment_method_object.logo}
                className="m-0 h-6 w-6"
                alt={`${scheduledPayment.paymentIntent.payment_method_object.brand} credit card logo`}
              />
              <p className="m-0">
                {scheduledPayment.paymentIntent.payment_method_object.last4}
              </p>
            </div>
          )
        } else {
          return (
            this.props.sale.card && (
              <div className="flex items-center space-x-1">
                <img
                  src={this.props.sale.card.logo}
                  className="m-0 h-6 w-6"
                  alt={`${this.props.sale.card.brand} credit card logo`}
                />
                <p className="m-0">{this.props.sale.card.last4}</p>
              </div>
            )
          )
        }
      case "check":
        if (scheduledPayment.paymentIntent) {
          return (
            <div className="flex space-x-1">{`Check ${scheduledPayment.paymentIntent.metadata.check_number}`}</div>
          )
        } else {
          return (
            <div className="flex space-x-1">{`${
              scheduledPayment.method && humanize(scheduledPayment.method)
            } ${this.props.sale.checkNumber || ""}`}</div>
          )
        }
      case "balanceapplication":
      case "balance_application":
        return <div className="flex space-x-1">-</div>
      default:
        return (
          <div className="flex space-x-1">
            {scheduledPayment.method && humanize(scheduledPayment.method)}
          </div>
        )
    }
  }

  renderTransactions() {
    const sortedPayments = [...this.props.sale.scheduledPayments].sort(
      (a, b) => {
        return new Date(a.createdAt) - new Date(b.createdAt)
      }
    )
    const lastId = sortedPayments[sortedPayments.length - 1].id

    return sortedPayments.map((scheduledPayment) => {
      return (
        <div key={scheduledPayment.id}>
          {this.shouldDisplayTransaction(scheduledPayment) && (
            <div className="mb-2 grid grid-cols-4 place-items-center gap-x-3 md:grid-cols-5 md:place-items-center">
              <div
                className={`badge justify-self-start ${badgeForPaymentStatus(
                  this.statusForPayment(scheduledPayment)
                )}`}
              >
                {humanize(this.statusForPayment(scheduledPayment))}
              </div>
              <div className="m-0">
                {this.renderPaymentMethod(scheduledPayment)}
              </div>
              <p className="m-0 hidden md:inline-flex">
                {moment(scheduledPayment.createdAt).format("MM/DD/YY")}
              </p>
              <p className="m-0 font-bold">
                {centsToDollars(scheduledPayment.totalPrice)}
              </p>
              {this.renderSaleStateActionButtons(scheduledPayment, lastId)}
            </div>
          )}
        </div>
      )
    })
  }

  renderSaleStateActionButtons(scheduledPayment, lastId) {
    const receiptButtonTitle = this.receiptPrintersAvailable()
      ? "Digital Receipt"
      : "Receipt"
    return (
      <div className="ml-2 mt-0 flex flex-col justify-end md:flex-row">
        {this.shouldDisplaySaleStateActions(scheduledPayment, lastId) && (
          <a
            className="btn btn-secondary mr-2"
            href={buildRoute($context.receiptUrl, {
              sale_id: this.props.sale.id,
            })}
          >
            {receiptButtonTitle}
          </a>
        )}
        {this.shouldDisplayActions(scheduledPayment) && (
          <div className="mt-0 flex flex-col justify-end md:flex-row">
            <PaymentSelectSaleModal
              scheduledPayment={scheduledPayment}
              handleProcessing={this.handleProcessing}
              sale={this.props.sale}
            />
            <SaleCancelModal
              scheduledPayment={scheduledPayment}
              handleProcessing={this.handleProcessing}
              sale={this.props.sale}
            />
          </div>
        )}
      </div>
    )
  }

  renderPrintReceiptButton() {
    if (
      this.receiptPrintersAvailable() &&
      this.isFinishedProcessing(this.props.sale)
    ) {
      return (
        <div>
          <hr />
          <div className="flex flex-row-reverse">
            <div className="my-2 flex shrink flex-row gap-2">
              <ReceiptPrinterChooser
                printers={$context.receiptPrinters}
                handlePrinterSelection={(printerId) =>
                  this.setState({ printerId })
                }
              />
              <NextButton
                processing={this.state.printingReceipt}
                icon="fa-kit fa-print mr-2"
                btnClass="btn btn-secondary self-center"
                text="Print Paper Receipt"
                processingText="Printing..."
                click={this.handlePrintReceipt}
              />
            </div>
          </div>
        </div>
      )
    }
  }

  handlePrintReceipt() {
    this.setState({ printingReceipt: true })
    // Note: jQuery ($) is loaded globally
    $.post(
      buildRoute($context.printPaperReceiptUrl, {
        sale_id: this.props.sale.id,
      }),
      { printer_id: this.state.printerId },
      (data) => {
        setTimeout(() => {
          this.setState({ printingReceipt: false })
        }, 2000) // we wait an additional 2 seconds to account for the delay with printer polling
      },
      "json"
    ).fail((data) => {
      const errorMessage = JSON.parse(data.responseText).error
      alert(
        `Error printing receipt: ${errorMessage}\n\nPlease check the printer and try again.`
      )
      this.setState({ printingReceipt: false })
    })
  }

  render() {
    return (
      <div>
        <div className="mt-1 flex justify-between px-3">
          <div className="flex">
            <button
              className="inline bg-white md:hidden"
              type="button"
              aria-label="return"
              id="al"
              onClick={() => this.props.setSelectedSale(null)}
            >
              <i className="fa-kit fa-chevron-left" />
            </button>
            <p className="m-0 font-semibold">#{this.props.sale.id}</p>
            <h2 className="hidden">Sale Details</h2>
          </div>
          <p className="m-0">
            {moment(this.props.sale.createdAt).format("MMM D YYYY, HH:mm")}
          </p>
        </div>
        <hr />
        <div className="flex justify-between px-3">
          <p className="m-0 text-sm font-semibold">Customer</p>
          {this.props.sale.contact && (
            <a
              className="m-0 text-blue-700"
              target="_blank"
              rel="noreferrer"
              href={`${$context.contactUrl}/${this.props.sale.contact.encodedId}`}
            >
              {this.props.sale.contact.name || "Customer Details"}
            </a>
          )}
          {!this.props.sale.contact && <span className="m-0">Guest</span>}
        </div>
        <hr />
        <div className="grid gap-x-3 px-3 md:gap-x-8">
          <p className="col-span-full m-0 place-self-start pb-5 text-sm font-semibold md:col-span-1 md:p-0">
            Payment
          </p>
          <div className="col-span-full md:col-span-4 md:col-start-3">
            {this.renderTransactions()}
          </div>
        </div>
        <hr />
        <h3 className="px-3 text-sm font-semibold">
          Items ({this.props.sale.posItems.length})
        </h3>
        <hr />
        {this.renderPosItems()}
        {this.renderCustomerNote()}
        {this.renderTotals()}
        {this.renderPrintReceiptButton()}
      </div>
    )
  }
}

SaleDetails.propTypes = {
  sale: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    totalPrice: PropTypes.number.isRequired,
    taxAmount: PropTypes.number,
    subtotal: PropTypes.number,
    refundedAmount: PropTypes.number,
    paymentType: PropTypes.string,
    note: PropTypes.string,
    createdAt: PropTypes.string,
    checkNumber: PropTypes.string,
    posItems: PropTypes.array.isRequired,
    scheduledPayments: PropTypes.array.isRequired,
    card: PropTypes.object,
    contact: PropTypes.shape({
      encodedId: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  setSelectedSale: PropTypes.func.isRequired,
}

// Add defaultProps for nested objects that might be null
SaleDetails.defaultProps = {
  sale: {
    posItems: [],
    scheduledPayments: [],
    card: null,
    contact: null,
  },
}

export default SaleDetails
